// src/app/settings/application/services/rounding-rules.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import {
  ApiPagedResponse,
  CreateRoundingRulePayload,
  DepartmentListItem,
  PayGroupListItem,
  RoundingRule,
  RoundingRulesResponse,
  UpdateRoundingRulePayload,
} from '../models/rounding-rules.model';

@Injectable({ providedIn: 'root' })
export class RoundingRulesService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get all rounding rules
   */
  getRoundingRules(): Observable<RoundingRule[] | null> {
    return this.http.get<RoundingRulesResponse>(`${this.baseUrl}/api/rounding-rules`).pipe(
      map((res) =>
        (res.data ?? []).map(
          (rule): RoundingRule => ({
            id: rule.id,
            payGroup: rule.payGroup
              ? {
                  id: rule.payGroup.id,
                  name: rule.payGroup.name,
                }
              : null,
            department: rule.department
              ? {
                  id: rule.department.id,
                  name: rule.department.name,
                  code: String(rule.department.code),
                }
              : null,
            roundingInterval: rule.roundingInterval,
            breakThresholdHours: Number(rule.breakThresholdHours ?? 0),
            breakTime: rule.breakTime,
            isDefault: !!rule.isDefault,
            createdAt: rule.createdAt,
            createdBy: rule.createdBy
              ? {
                  id: rule.createdBy.id,
                  firstName: rule.createdBy.firstName ?? '',
                  lastName: rule.createdBy.lastName ?? '',
                  employeeNumber: rule.createdBy.employeeNumber ?? null,
                }
              : null,
            updatedAt: rule.updatedAt ?? null,
            updatedBy: rule.updatedBy
              ? {
                  id: rule.updatedBy.id,
                  firstName: rule.updatedBy.firstName ?? '',
                  lastName: rule.updatedBy.lastName ?? '',
                  employeeNumber: rule.updatedBy.employeeNumber ?? null,
                }
              : null,
          }),
        ),
      ),
      catchError((err) => {
        console.error('Error fetching rounding rules:', err);
        return of(null);
      }),
    );
  }

  /**
   * Create rounding rule
   */
  createRoundingRule(payload: CreateRoundingRulePayload): Observable<boolean> {
    return this.http.post<{ status: string }>(`${this.baseUrl}/api/rounding-rules`, payload).pipe(
      map(() => true),
      catchError((err) => {
        console.error('Error creating rounding rule:', err);
        return of(false);
      }),
    );
  }

  /**
   * Update rounding rule
   */
  updateRoundingRule(id: number, payload: UpdateRoundingRulePayload): Observable<boolean> {
    return this.http.patch<{ status: string }>(`${this.baseUrl}/api/rounding-rules/${id}`, payload).pipe(
      map(() => true),
      catchError((err) => {
        console.error('Error updating rounding rule:', err);
        return of(false);
      }),
    );
  }

  /**
   * Make rounding rule default
   */
  makeDefaultRoundingRule(id: number): Observable<boolean> {
    return this.http
      .patch<{ status: string }>(`${this.baseUrl}/api/rounding-rules/${id}/make-default`, {})
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error making rounding rule default:', err);
          return of(false);
        }),
      );
  }

  /**
   * Delete rounding rule
   */
  deleteRoundingRule(id: number): Observable<boolean> {
    return this.http.delete<{ status: string }>(`${this.baseUrl}/api/rounding-rules/${id}`).pipe(
      map(() => true),
      catchError((err) => {
        console.error('Error deleting rounding rule:', err);
        return of(false);
      }),
    );
  }

  /**
   * Get all departments across pages
   */
  getAllDepartments(pageSize: number = 200): Observable<DepartmentListItem[]> {
    const fetchPage = (pageNumber: number) => {
      const params = new HttpParams()
        .set('pageNumber', pageNumber)
        .set('pageSize', pageSize)
        .set('orderBy', 'codeAsc');

      return this.http.get<ApiPagedResponse<any>>(`${this.baseUrl}/api/departments`, { params });
    };

    return fetchPage(1).pipe(
      expand((res) => (res.currentPage < res.totalPages ? fetchPage(res.currentPage + 1) : of())),
      map((res: ApiPagedResponse<any> | undefined) => res?.data ?? []),
      reduce((acc: any[], pageItems: any[]) => acc.concat(pageItems), []),
      map((rows) =>
        rows.map(
          (d): DepartmentListItem => ({
            id: d.id,
            name: d.name,
            code: String(d.code),
          }),
        ),
      ),
      catchError((err) => {
        console.error('Error fetching departments:', err);
        return of([]);
      }),
    );
  }

  /**
   * Get all pay groups
   */
  getAllPayGroups(): Observable<PayGroupListItem[]> {
    return this.http.get<ApiPagedResponse<any>>(`${this.baseUrl}/api/pay-groups`).pipe(
      map((res) =>
        (res.data ?? []).map(
          (pg): PayGroupListItem => ({
            id: pg.id,
            name: pg.name,
            description: pg.description ?? null,
            isOvertimeEligible: !!pg.isOvertimeEligible,
            overtimeThresholdHours: Number(pg.overtimeThresholdHours ?? 0),
          }),
        ),
      ),
      catchError((err) => {
        console.error('Error fetching pay groups:', err);
        return of([]);
      }),
    );
  }
}